import React, { useEffect, useState } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton, Fade } from '@mui/material';
import { DialogActions, Button } from '@mui/material';
import AppointmentCard from './AppointmentCard';
import ErrorDialog from '../../../global/ErrorDialog';
import { Appointment, isRealAppointment } from '../../../../types';
import { Dayjs } from '../../../../utils/dayjs-config';
import LoadingSpinner from '../../../global/LoadingSpinner';
import CloseIcon from '@mui/icons-material/Close';
import { useResultSnackbar } from '../../../global/SuccessOrErrorSnackbar';

interface AppointmentSectionProps {
    date: Dayjs;
    appointments: Appointment[];
    employeeAppointmentsMap: Record<string, string[]>;
    isLoading?: boolean;
}

const AppointmentSection: React.FC<AppointmentSectionProps> = ({
    date,
    appointments,
    employeeAppointmentsMap,
    isLoading,
}) => {
    const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [handoffSuccessDialogOpen, setHandoffSuccessDialogOpen] = useState(false);
    const { showSuccess, showError, snackbar } = useResultSnackbar({
        successTitle: 'Schedule Updated!',
        errorTitle: 'Something Went Wrong',
        autoHideDuration: 2500,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    });
    const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);

    const handleErrorClose = () => {
        setError(null);
        setErrorDialogOpen(false);
    };
    const handleHandoffSuccess = () => setHandoffSuccessDialogOpen(true);
    const handleOpenAppointmentDetails = (appointment: Appointment) => {
        setSelectedAppointment(appointment);
        setAppointmentDialogOpen(true);
    };
    const handleCloseAppointmentDetails = () => {
        setAppointmentDialogOpen(false);
    };

    useEffect(() => {
        try {
            setError(null);

            // Format the date as 'MMDDYYYY' for consistency with the employeeAppointmentsMap keys
            const formattedDate = date.format('MMDDYYYY');
            const appointmentIdsForDate = employeeAppointmentsMap[formattedDate] || [];
            const dateAppointments = appointments
                .filter(app => appointmentIdsForDate.includes(app.id))
                .filter(isRealAppointment)
                .sort((a, b) => a.appointmentTime.localeCompare(b.appointmentTime));

            setFilteredAppointments(dateAppointments);
        } catch (err) {
            setError("Failed to load appointments. Please try again.");
            setErrorDialogOpen(true);
            console.error("Error loading appointments:", err);
        }
    }, [date, appointments, employeeAppointmentsMap]);
    return (
        <>
            <ErrorDialog open={errorDialogOpen} onClose={handleErrorClose} message={error} />

            <Typography variant="h6" align="center" color="gray">APPOINTMENTS</Typography>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                    <LoadingSpinner />
                </Box>
            ) : filteredAppointments.length > 0 ? (
                filteredAppointments.map(appointment => (
                    <AppointmentCard
                        key={appointment.id}
                        appointment={appointment}
                        onOpenDetails={handleOpenAppointmentDetails}
                        showSuccess={showSuccess}
                        showError={showError}
                        onHandoffSuccess={handleHandoffSuccess}
                    />
                ))
            ) : (
                <Typography align="center" sx={{ mt: 2 }}>No appointments</Typography>
            )}

            <Dialog
                open={appointmentDialogOpen}
                onClose={handleCloseAppointmentDetails}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Fade}
                TransitionProps={{
                    timeout: 220,
                    onExited: () => setSelectedAppointment(null),
                }}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 2,
                        width: '100%',
                        m: 0,
                        mx: 1,
                        height: 'auto',
                        maxHeight: { xs: '92dvh', sm: 'calc(100% - 64px)' },
                    }
                }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    Manage Appointment
                    <IconButton onClick={handleCloseAppointmentDetails} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {selectedAppointment && (
                    <DialogContent sx={{ p: { xs: 0, sm: 2 } }}>
                        <AppointmentCard
                            appointment={selectedAppointment}
                            fullWidth
                            defaultExpanded
                            renderAsDialogContent
                            showSuccess={showSuccess}
                            showError={showError}
                            onHandoffSuccess={handleHandoffSuccess}
                        />
                    </DialogContent>
                )}
            </Dialog>
            <Dialog
                open={handoffSuccessDialogOpen}
                onClose={() => setHandoffSuccessDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Appointment Handed Off</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        The appointment was successfully handed off to the selected staff member.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setHandoffSuccessDialogOpen(false)}
                        color="primary"
                        variant="contained"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {snackbar}
        </>
    );
};

export default AppointmentSection;
